import { useEffect, useState, useCallback, useMemo, createContext } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import isEmpty from 'lodash.isempty'

import { Types } from '../../store/ducks/consultant'

import { botTextChatMessageFactory, botTipsChatMessageFactory, optionsFactory, userTextChatMessageFactory } from './utils'
import {
  afterGetUserEmailMessageText,
  afterResetTextMessage,
  askForFeedbackMessageText,
  finalThanksMessageText,
  finalThanksMessageText2,
  finalThanksMessageText3,
  likeButtonText,
  messageBeforeShowResult,
  offerSelectedWinesToMessageText,
  positiveFeedbackResponseMessageText,
  receiveTipsAndDealsByEmailAdviceText,
  restartButtonText,
  seeMoreButtonText,
  seeMoreFeedbackResponseMessageText,
  seeSelectWinesButtonText,
  showFinalThanksButtonText
} from './literals'
import { useChatMessages } from './useChatMessages'
import { CONSULTANT_TYPE, INITIAL_CHAT_MESSAGES, INITIAL_RESULT_OPTIONS, POST_WITH_SELECTED_WINES_URL } from './config'
import { useBotTyping } from './useBotTyping'
import { subscribeToNewsletter } from '../../store/httpServices'

export const botContext = createContext({
  chatMessages: [],
  isLoading: true,
  userInput: null
})

const BotContextProvider = ({ children }) => {
  const dispatch = useDispatch()
  const { data, activityId, loading } = useSelector((state) => state.consultant)

  const [userInput, setUserInput] = useState(null)
  const { isBotTyping, showTypingIndicator, stopTypingIndicator } = useBotTyping()
  const [isSubscribingToNewsletter, setIsSubscribingToNewsletter] = useState(false)
  const { chatMessages, appendMessagesToChat, resetChatMessages } = useChatMessages(INITIAL_CHAT_MESSAGES.map(botTextChatMessageFactory))

  const isLoading = useMemo(() => loading || isBotTyping || isSubscribingToNewsletter, [loading, isBotTyping, isSubscribingToNewsletter])

  const parsePrice = useCallback((price) => {
    const sanitizedPriceString = String(price).replace(/[A-Z,a-z,\s]/g, '')
    const parsedPrice = Number(sanitizedPriceString).toFixed(2).replace('.', ',')

    return parsedPrice
  }, [])

  const parseTips = useCallback((tips) => tips.map((item) => ({
    ...item,
    title: item.name,
    price: parsePrice(item.price),
    salePrice: item.salePrice && parsePrice(item.salePrice),
  })), [parsePrice])

  const restartChat = useCallback(() => {
    resetChatMessages([botTextChatMessageFactory(afterResetTextMessage)])
    setUserInput(null)
    stopTypingIndicator()
    dispatch({
      type: Types.RESET_STORE,
      consultantId: CONSULTANT_TYPE,
    })
    dispatch({
      type: Types.REQUEST_STUFF,
      consultantId: CONSULTANT_TYPE,
    })
  }, [dispatch, resetChatMessages, stopTypingIndicator])

  const showFinalThanks = useCallback(async ({ name }) => {
    setUserInput(null)
    appendMessagesToChat(
      userTextChatMessageFactory(name)
    )

    await showTypingIndicator()

    appendMessagesToChat(
      botTextChatMessageFactory(finalThanksMessageText),
      botTextChatMessageFactory(finalThanksMessageText2),
      botTextChatMessageFactory(finalThanksMessageText3),
    )
  }, [appendMessagesToChat, showTypingIndicator])

  const handleUserEmail = useCallback(async (email) => {
    appendMessagesToChat(userTextChatMessageFactory(email))
    setUserInput(null)

    setIsSubscribingToNewsletter(true)

    await subscribeToNewsletter({ email, activityId })

    setIsSubscribingToNewsletter(false)

    appendMessagesToChat(botTextChatMessageFactory(afterGetUserEmailMessageText))

    await showTypingIndicator()

    appendMessagesToChat(botTextChatMessageFactory(offerSelectedWinesToMessageText))

    setUserInput({
      type: 'options', options: [{
        name: seeSelectWinesButtonText,
        options: {
          type: 'link',
          url: POST_WITH_SELECTED_WINES_URL,
        }
      }, {
        name: showFinalThanksButtonText,
        onClick: showFinalThanks
      }]
    })
  }, [activityId, appendMessagesToChat, showFinalThanks, showTypingIndicator])

  const feedbackHandlerFactory = useCallback((tips, firstTipsCount) => async (option) => {
    appendMessagesToChat(userTextChatMessageFactory(option.name))

    const otherTips = parseTips(tips.slice(firstTipsCount, tips.length))

    await showTypingIndicator()

    switch (option.name) {
      case likeButtonText:
        appendMessagesToChat(botTextChatMessageFactory(positiveFeedbackResponseMessageText))
        setUserInput({
          type: 'input',
          onSubmit: handleUserEmail,
          adviceText: receiveTipsAndDealsByEmailAdviceText,
          placeholder: 'Email',
          inputType: 'email'
        })
        break
      case seeMoreButtonText:
        appendMessagesToChat(
          botTextChatMessageFactory(seeMoreFeedbackResponseMessageText(otherTips.length > 1)),
          botTipsChatMessageFactory(otherTips),
        )
        const handleFeedback = feedbackHandlerFactory(tips, firstTipsCount)
        setUserInput({ type: 'options', options: optionsFactory(INITIAL_RESULT_OPTIONS, handleFeedback) })
        break
      case restartButtonText:
        restartChat()
        break
      default:
        break
    }
  }, [appendMessagesToChat, parseTips, showTypingIndicator, handleUserEmail, restartChat])

  const askForFeedback = useCallback(async (tips, firstTipsCount) => {
    const hasMoreTips = tips.length > firstTipsCount
    const feedbackOptions = [...INITIAL_RESULT_OPTIONS]

    if (hasMoreTips) {
      const seeMoreOption = { name: seeMoreButtonText }

      feedbackOptions.splice(1, 0, seeMoreOption)
    }

    await showTypingIndicator()

    appendMessagesToChat(botTextChatMessageFactory(askForFeedbackMessageText))

    const handleFeedback = feedbackHandlerFactory(tips, firstTipsCount)
    setUserInput({ type: 'options', options: optionsFactory(feedbackOptions, handleFeedback) })
  }, [appendMessagesToChat, feedbackHandlerFactory, showTypingIndicator])

  const showTips = useCallback(async (tips) => {
    const firstTipsCount = 3
    const firstTips = parseTips(tips.slice(0, firstTipsCount))

    await showTypingIndicator()

    const isPlural = firstTips.length > 1
    appendMessagesToChat(
      botTextChatMessageFactory(messageBeforeShowResult(isPlural))
    )

    await showTypingIndicator()

    appendMessagesToChat(botTipsChatMessageFactory(firstTips))

    await askForFeedback(tips, firstTipsCount)
  }, [parseTips, showTypingIndicator, appendMessagesToChat, askForFeedback])

  const handleNextStep = useCallback((option) => {
    appendMessagesToChat(userTextChatMessageFactory(option.name))

    dispatch({
      type: Types.REQUEST_STUFF,
      step: data?.step + 1,
      optionId: option.id,
      option,
      activityId,
      consultantId: CONSULTANT_TYPE,
      choices: data?.choices,
    })
  }, [activityId, appendMessagesToChat, data?.choices, data?.step, dispatch])

  useEffect(() => {
    dispatch({
      type: Types.REQUEST_STUFF,
      consultantId: CONSULTANT_TYPE,
    })
  }, [dispatch])

  useEffect(() => {
    const currentOptions = data?.options || []
    const isSkippable = data?.skippable

    const orderedOptions = [...currentOptions]

    if (orderedOptions && orderedOptions.length >= 8 && isSkippable) {
      const whatever = orderedOptions.shift()
      orderedOptions.push(whatever)
    }

    setUserInput({ type: 'options', options: optionsFactory(orderedOptions, handleNextStep) })
  }, [data, handleNextStep])


  useEffect(() => {
    const hasTips = !isEmpty(data?.items || [])

    if (hasTips) {
      showTips(data.items)
    } else if (data?.text) {
      appendMessagesToChat(botTextChatMessageFactory(data.text))
    }
  }, [data, showTips, appendMessagesToChat, showTypingIndicator])

  useEffect(() => {
    return () => {
      dispatch({
        type: Types.RESET_STORE,
      })
    }
  }, [dispatch])

  return (
    <botContext.Provider value={{
      chatMessages,
      isLoading,
      userInput,
    }}>
      {children}
    </botContext.Provider>
  )
}

export default BotContextProvider
